import React, { useState, useEffect, useMemo } from 'react';
import { listUsers, disableUser, enableUser, updateUserGroups } from '../../services/api';

const ALL_GROUPS = [
  'admin', 'user', 'finance', 'investor',
  'security', 'growth', 'senior-leader',
  'div-ms1', 'div-ms2', 'div-is', 'div-bl', 'div-corp',
];

const STATUS_DISPLAY = {
  CONFIRMED: { label: 'Enabled', className: 'active' },
  DISABLED: { label: 'Disabled', className: 'critical' },
};

const GROUP_STYLE = {
  background: 'var(--bcore-surface-tag)',
  color: 'var(--bcore-text-muted)',
  fontSize: '10px',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  padding: '3px 8px',
  borderRadius: '100px',
  whiteSpace: 'nowrap',
};

function formatDate(iso) {
  if (!iso) return '--';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function formatRelative(iso) {
  if (!iso) return '--';
  const now = new Date();
  const d = new Date(iso);
  const diffMs = now - d;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 30) return `${diffD}d ago`;
  return formatDate(iso);
}

function Users({ user }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [groupFilter, setGroupFilter] = useState('All');
  const [sortField, setSortField] = useState('email');
  const [sortDir, setSortDir] = useState('asc');
  const [disablingUser, setDisablingUser] = useState(null);
  const [enablingUser, setEnablingUser] = useState(null);
  const [managingUser, setManagingUser] = useState(null);
  const [pendingGroups, setPendingGroups] = useState([]);
  const [savingGroups, setSavingGroups] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function fetchUsers() {
      setLoading(true);
      setError('');
      try {
        const data = await listUsers();
        if (!cancelled) setUsers(Array.isArray(data) ? data : []);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load users');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchUsers();
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    let result = [...users];

    if (statusFilter !== 'All') {
      const target = statusFilter === 'Enabled' ? 'CONFIRMED' : 'DISABLED';
      result = result.filter((u) => u.status === target);
    }

    if (groupFilter !== 'All') {
      result = result.filter((u) => u.groups?.includes(groupFilter));
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter((u) =>
        (u.email || '').toLowerCase().includes(q) ||
        (u.username || '').toLowerCase().includes(q)
      );
    }

    result.sort((a, b) => {
      let valA, valB;
      switch (sortField) {
        case 'email':
          valA = (a.email || '').toLowerCase();
          valB = (b.email || '').toLowerCase();
          break;
        case 'status':
          valA = a.status || '';
          valB = b.status || '';
          break;
        case 'created':
          valA = new Date(a.createdAt || 0);
          valB = new Date(b.createdAt || 0);
          break;
        case 'lastAuth':
          valA = new Date(a.lastAuthAt || 0);
          valB = new Date(b.lastAuthAt || 0);
          break;
        default:
          valA = (a.email || '').toLowerCase();
          valB = (b.email || '').toLowerCase();
      }
      if (valA < valB) return sortDir === 'asc' ? -1 : 1;
      if (valA > valB) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [users, statusFilter, groupFilter, searchQuery, sortField, sortDir]);

  function handleSort(field) {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  }

  function sortIndicator(field) {
    if (sortField !== field) return '';
    return sortDir === 'asc' ? ' \u2191' : ' \u2193';
  }

  async function handleDisable(targetUser) {
    const confirmed = window.confirm(
      `Disable user "${targetUser.email}"?\n\nThis will immediately revoke all active sessions and prevent future logins.`
    );
    if (!confirmed) return;

    setDisablingUser(targetUser.username);
    setError('');
    try {
      await disableUser(targetUser.username, 'Disabled via User Management');
      setUsers((prev) =>
        prev.map((u) =>
          u.username === targetUser.username ? { ...u, status: 'DISABLED' } : u
        )
      );
    } catch (err) {
      setError(err.message || 'Failed to disable user');
    } finally {
      setDisablingUser(null);
    }
  }

  async function handleEnable(targetUser) {
    const confirmed = window.confirm(
      `Re-enable user "${targetUser.email}"?\n\nThis will allow the user to log in again.`
    );
    if (!confirmed) return;

    setEnablingUser(targetUser.username);
    setError('');
    try {
      await enableUser(targetUser.username);
      setUsers((prev) =>
        prev.map((u) =>
          u.username === targetUser.username ? { ...u, status: 'CONFIRMED' } : u
        )
      );
    } catch (err) {
      setError(err.message || 'Failed to enable user');
    } finally {
      setEnablingUser(null);
    }
  }

  function openGroupManager(targetUser) {
    setManagingUser(targetUser.username);
    setPendingGroups([...(targetUser.groups || [])]);
  }

  function closeGroupManager() {
    setManagingUser(null);
    setPendingGroups([]);
    setSavingGroups(false);
  }

  function toggleGroup(group) {
    setPendingGroups((prev) =>
      prev.includes(group) ? prev.filter((g) => g !== group) : [...prev, group]
    );
  }

  async function handleSaveGroups() {
    const targetUser = users.find((u) => u.username === managingUser);
    if (!targetUser) return;

    const currentGroups = targetUser.groups || [];
    const groupsToAdd = pendingGroups.filter((g) => !currentGroups.includes(g));
    const groupsToRemove = currentGroups.filter((g) => !pendingGroups.includes(g));

    if (groupsToAdd.length === 0 && groupsToRemove.length === 0) {
      closeGroupManager();
      return;
    }

    setSavingGroups(true);
    setError('');
    try {
      await updateUserGroups(managingUser, groupsToAdd, groupsToRemove);
      setUsers((prev) =>
        prev.map((u) =>
          u.username === managingUser ? { ...u, groups: [...pendingGroups] } : u
        )
      );
      closeGroupManager();
    } catch (err) {
      setError(err.message || 'Failed to update groups');
      setSavingGroups(false);
    }
  }

  const currentUserEmail = user?.email;
  const activeFilterCount = (statusFilter !== 'All' ? 1 : 0) + (groupFilter !== 'All' ? 1 : 0);

  return (
    <div>
      {/* Toolbar */}
      <div className="page-toolbar">
        <div className="page-toolbar-left">
          <div className="search-wrapper">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              className="search-input"
              placeholder="Search by email or username..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <select
            className="filter-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="All">All Status</option>
            <option value="Enabled">Enabled</option>
            <option value="Disabled">Disabled</option>
          </select>
          <select
            className="filter-select"
            value={groupFilter}
            onChange={(e) => setGroupFilter(e.target.value)}
          >
            <option value="All">All Groups</option>
            {ALL_GROUPS.map((g) => (
              <option key={g} value={g}>{g}</option>
            ))}
          </select>
          {activeFilterCount > 0 && (
            <button
              className="btn btn-outline btn-sm"
              onClick={() => { setStatusFilter('All'); setGroupFilter('All'); }}
            >
              Clear Filters
            </button>
          )}
        </div>
        <div className="page-toolbar-right">
          <span style={{
            fontSize: '12px',
            color: 'var(--bcore-text-dim)',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}>
            {filtered.length} user{filtered.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="form-api-error">{error}</div>
      )}

      {/* Loading State */}
      {loading && (
        <div style={{
          textAlign: 'center',
          padding: '40px',
          color: 'var(--bcore-text-dim)',
          fontSize: '13px',
          textTransform: 'uppercase',
          letterSpacing: '1px',
        }}>
          Loading users...
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && filtered.length === 0 && (
        <div style={{
          textAlign: 'center',
          padding: '60px 20px',
          color: 'var(--bcore-text-dim)',
          fontSize: '13px',
        }}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ marginBottom: '16px', opacity: 0.4 }}>
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
          </svg>
          <div style={{ textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px' }}>
            {users.length === 0 ? 'No users found' : 'No matching users'}
          </div>
          <div style={{ fontSize: '12px' }}>
            {users.length === 0
              ? 'Users will appear here once the backend API is connected'
              : 'Try adjusting your search or filters'}
          </div>
        </div>
      )}

      {/* Data Table */}
      {!loading && filtered.length > 0 && (
        <div className="data-table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => handleSort('email')}
                >
                  Email{sortIndicator('email')}
                </th>
                <th
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => handleSort('status')}
                >
                  Status{sortIndicator('status')}
                </th>
                <th>Groups</th>
                <th
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => handleSort('created')}
                >
                  Created{sortIndicator('created')}
                </th>
                <th
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => handleSort('lastAuth')}
                >
                  Last Login{sortIndicator('lastAuth')}
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => {
                const statusInfo = STATUS_DISPLAY[u.status] || { label: u.status, className: '' };
                const isSelf = u.email === currentUserEmail;
                const isDisabled = u.status === 'DISABLED';
                const isManaging = managingUser === u.username;

                return (
                  <React.Fragment key={u.username}>
                    <tr>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span>{u.email}</span>
                          {isSelf && (
                            <span style={{
                              ...GROUP_STYLE,
                              background: 'var(--bcore-accent)',
                              color: 'var(--bcore-bg-primary)',
                              fontSize: '9px',
                            }}>
                              You
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        <span className={`badge-status ${statusInfo.className}`}>
                          <span className="badge-dot"></span>
                          {statusInfo.label}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                          {(u.groups || []).map((g) => (
                            <span key={g} style={GROUP_STYLE}>{g}</span>
                          ))}
                          {(!u.groups || u.groups.length === 0) && (
                            <span style={{ fontSize: '11px', color: 'var(--bcore-text-dim)' }}>--</span>
                          )}
                        </div>
                      </td>
                      <td title={u.createdAt}>{formatDate(u.createdAt)}</td>
                      <td title={u.lastAuthAt}>{formatRelative(u.lastAuthAt)}</td>
                      <td>
                        <div className="table-actions">
                          {/* Manage Groups */}
                          <button
                            className="btn btn-outline btn-sm"
                            onClick={() => isManaging ? closeGroupManager() : openGroupManager(u)}
                            title="Manage Groups"
                          >
                            {isManaging ? 'Cancel' : 'Groups'}
                          </button>

                          {/* Enable / Disable */}
                          {isDisabled ? (
                            <button
                              className="btn btn-outline btn-sm"
                              style={{ borderColor: 'var(--status-active)', color: 'var(--status-active)' }}
                              onClick={() => handleEnable(u)}
                              disabled={enablingUser === u.username}
                            >
                              {enablingUser === u.username ? 'Enabling...' : 'Enable'}
                            </button>
                          ) : !isSelf ? (
                            <button
                              className="btn btn-outline btn-sm"
                              style={{ borderColor: 'var(--status-critical)', color: 'var(--status-critical)' }}
                              onClick={() => handleDisable(u)}
                              disabled={disablingUser === u.username}
                            >
                              {disablingUser === u.username ? 'Disabling...' : 'Disable'}
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>

                    {/* Group Management Panel */}
                    {isManaging && (
                      <tr>
                        <td colSpan={6} style={{ padding: '16px', background: 'var(--bcore-hover-subtle)' }}>
                          <div style={{ marginBottom: '12px', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--bcore-text-muted)' }}>
                            Manage Groups for {u.email}
                          </div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px' }}>
                            {ALL_GROUPS.map((g) => {
                              const isChecked = pendingGroups.includes(g);
                              return (
                                <label
                                  key={g}
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    padding: '6px 12px',
                                    borderRadius: '100px',
                                    border: `1px solid ${isChecked ? 'var(--bcore-accent)' : 'var(--bcore-border)'}`,
                                    background: isChecked ? 'rgba(214, 226, 0, 0.1)' : 'transparent',
                                    color: isChecked ? 'var(--bcore-accent)' : 'var(--bcore-text-dim)',
                                    fontSize: '11px',
                                    fontWeight: 600,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                    cursor: 'pointer',
                                    transition: 'all 0.15s',
                                    fontFamily: 'JetBrains Mono, monospace',
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={() => toggleGroup(g)}
                                    style={{ display: 'none' }}
                                  />
                                  {isChecked && (
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                      <polyline points="20 6 9 17 4 12" />
                                    </svg>
                                  )}
                                  {g}
                                </label>
                              );
                            })}
                          </div>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button
                              className="btn btn-primary btn-sm"
                              onClick={handleSaveGroups}
                              disabled={savingGroups}
                            >
                              {savingGroups ? 'Saving...' : 'Save Groups'}
                            </button>
                            <button
                              className="btn btn-outline btn-sm"
                              onClick={closeGroupManager}
                              disabled={savingGroups}
                            >
                              Cancel
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default Users;
